import { Relation } from '@cotera/era';
import { queryKeys } from '@cotera/client/app/hooks/query-cache-keys';
import { DuckDBQueryResult } from '../duckdb';
import { CancelablePromise, SummarizeRecord } from '@cotera/client/app/etc';
import {
  useAppData,
  useTenantedClient,
  useWhoami,
} from '@cotera/client/app/stores/org';
import { DataProvider, DataResult } from './fn.type';
import {
  getOrCreateArtifact,
  getUsableArtifactCache,
  useGetOrCreateArtifact,
} from './artifacts';
import { useQueryClient, useSuspenseQuery } from '@tanstack/react-query';
import { Assert } from '@cotera/utilities';
import { useAbortController } from './hook';
import { RateLimiter, Semaphore } from '../semaphore';
import { useInvalidateArtifactById } from '../../hooks/artifacts';
import { useEraScopesAwareRelIR } from '../../pages/apps/compiler/macro-expansion/scopes';
import { useGetAccessToken } from '@cotera/client/app/hooks/use-access-token';

const USABLE_ARTIFACT_CACHE_FRESHNESS_MS = 5 * 1000; //5 seconds

const defaultRateLimiter = new Semaphore(5);

export type QueryData = {
  data: DuckDBQueryResult;
  artifactRel: Relation;
  fromArtifactId: string | null;
  createdAt?: Date;
};

export const useLoadArtifact: DataProvider<
  {
    rel: Relation;
  },
  QueryData & {
    refetch: () => Promise<void>;
  }
> = ({ rel }, opts) => {
  const orgId = useWhoami((x) => x.org.id);
  const queryClient = useQueryClient();
  const abort = useAbortController({
    abortOnUnmount: true,
  });
  const res = useGetOrCreateArtifact(
    { rel },
    {
      abort: opts?.abort ?? abort,
    }
  );

  const artifact = res.data;

  const invalidate = useInvalidateArtifactById();

  return useSuspenseQuery({
    queryKey: queryKeys.artifacts.artifact({
      orgId,
      rel: rel,
    }),
    queryFn: async () => {
      if (artifact.isErr()) {
        throw new Error(artifact.error.message);
      }

      const artifactRel = Relation.fromAst(artifact.value.ir);

      const unwrapMeta = () => {
        if (artifact.value.meta?.t === 'file') {
          return artifact.value.meta;
        }

        return null;
      };

      const unwrappedMeta = unwrapMeta();

      return {
        artifactRel,
        fromArtifactId: unwrappedMeta?.artifactId ?? null,
        createdAt: unwrappedMeta?.createdAt ?? null,
        refetch: async () => {
          await queryClient.invalidateQueries({
            queryKey: queryKeys.artifacts.forRel({ rel: rel, orgId }),
          });
          await queryClient.invalidateQueries({
            queryKey: queryKeys.artifacts.artifact({
              orgId,
              rel: rel,
            }),
          });
          await invalidate.mutateAsync({
            id: artifact.isOk() ? unwrappedMeta?.artifactId ?? null : null,
          });
        },
      };
    },
  });
};

export const useDuckDBQuery: DataProvider<
  {
    rel: Relation;
    rateLimiter?: RateLimiter;
  },
  QueryData
> = ({ rateLimiter = defaultRateLimiter, ...props }, opts) => {
  return useArtifactQuery(
    {
      baseRel: props.rel,
      rel: (rel) => rel,
      rateLimiter,
    },
    opts
  );
};

export const useArtifactQuery: DataProvider<
  {
    baseRel: Relation;
    rel: (rel: Relation) => Relation;
    limit?: number;
    rateLimiter?: RateLimiter;
  },
  QueryData
> = (props, opts) => {
  const orgId = useWhoami((x) => x.org.id);
  const api = useTenantedClient();
  const queryClient = useQueryClient();
  const initedDb = useAppData((s) => s.initedDb);
  const getAccessToken = useGetAccessToken();
  const rateLimiter = props.rateLimiter ?? defaultRateLimiter;
  const rel = props.rel(Relation.wrap(useEraScopesAwareRelIR(props.baseRel)));

  const abort = useAbortController({
    abortOnUnmount: true,
  });

  return useSuspenseQuery({
    staleTime: 0,
    gcTime: 30 * 1000, // 30 seconds - prevent premature GC during Suspense transitions
    refetchOnMount: 'always',
    queryKey: queryKeys.artifacts.data({
      orgId,
      rel: rel,
    }),
    queryFn: async () => {
      try {
        const existingCache = await getUsableArtifactCache({
          initedDb,
          api,
          queryClient,
          orgId,
          abort: opts?.abort ?? abort,
          freshnessMs: USABLE_ARTIFACT_CACHE_FRESHNESS_MS,
          getAccessToken,
        });

        const artifact = await getOrCreateArtifact({
          rel: rel,
          initedDb,
          existingCache,
          api,
          queryClient,
          orgId,
          opts: opts ?? {
            abort: abort,
          },
          getAccessToken,
        });

        await rateLimiter.acquire();

        const db = await initedDb;

        const artifactRes = Assert.assertOk(artifact);

        const artifactRel = Relation.wrap(artifactRes.ir);

        const data = await new CancelablePromise(db.query(artifactRel), {
          controller: opts?.abort ?? abort,
        });

        const result = new DuckDBQueryResult(data, artifactRel.attributes);

        return {
          data: result,
          artifactRel,
          fromArtifactId:
            artifactRes?.meta?.t === 'file'
              ? artifactRes.meta.artifactId
              : null,
          createdAt:
            artifactRes?.meta?.t === 'file'
              ? artifactRes.meta.createdAt
              : undefined,
        };
      } finally {
        await rateLimiter.release();
      }
    },
  });
};

export const useSummariseQuery: DataProvider<
  {
    baseRel: Relation;
    rel?: (rel: Relation) => Relation;
    rateLimiter?: RateLimiter;
  },
  QueryData
> = (
  props,
  opts
): DataResult<{
  data: DuckDBQueryResult<SummarizeRecord>;
  artifactRel: Relation;
  fromArtifactId: string | null;
  createdAt?: Date;
}> => {
  const relFn = props.rel ?? ((rel) => rel);
  const orgId = useWhoami((x) => x.org.id);
  const api = useTenantedClient();
  const queryClient = useQueryClient();
  const getAccessToken = useGetAccessToken();
  const artifactRel = relFn(
    Relation.wrap(useEraScopesAwareRelIR(props.baseRel))
  );
  const abort = useAbortController({
    abortOnUnmount: true,
  });
  const initedDb = useAppData((s) => s.initedDb);

  return useSuspenseQuery({
    staleTime: 0,
    gcTime: 30 * 1000, // 30 seconds - prevent premature GC during Suspense transitions
    refetchOnMount: 'always',
    queryKey: [
      ...queryKeys.artifacts.data({
        orgId: useWhoami((x) => x.org.id),
        rel: artifactRel,
      }),
      'summarize',
    ],
    queryFn: async () => {
      const existingCache = await getUsableArtifactCache({
        initedDb,
        api,
        queryClient,
        orgId,
        abort: opts?.abort ?? abort,
        freshnessMs: USABLE_ARTIFACT_CACHE_FRESHNESS_MS,
        getAccessToken,
      });

      const artifact = await getOrCreateArtifact({
        rel: artifactRel,
        initedDb,
        existingCache,
        api,
        queryClient,
        orgId,
        opts: opts ?? {
          abort: abort,
        },
        getAccessToken,
      });

      const artifactRes = Assert.assertOk(artifact);

      const db = await initedDb;
      const data = await new CancelablePromise(db.summarize(artifactRel), {
        controller: opts?.abort ?? abort,
      });
      return {
        data,
        artifactRel,
        ...artifactRes.meta,
      };
    },
  });
};
